/**
 * Centralized Environment Configuration for Next.js Client
 *
 * Provides environment-aware configuration based on NODE_ENV.
 * Supports development, staging, and production environments.
 */

export type NodeEnv = 'development' | 'staging' | 'production';

/**
 * Get the current Node environment
 * Checks NEXT_PUBLIC_APP_ENV first (for manual overrides on hosting platforms), 
 * then falls back to NODE_ENV.
 * Defaults to 'development' if neither is set.
 */
export function getNodeEnv(): NodeEnv {
    const appEnv = process.env.NEXT_PUBLIC_APP_ENV as string | undefined;
    if (appEnv === 'development' || appEnv === 'staging' || appEnv === 'production') {
        return appEnv;
    }
    const nodeEnv = process.env.NODE_ENV as string | undefined;
    if (nodeEnv === 'staging' || nodeEnv === 'production') {
        return nodeEnv;
    }

    return 'development';
}

/**
 * Get environment variable suffix based on NODE_ENV
 */
function getEnvSuffix(): '_DEV' | '_STAGING' | '_PROD' {
    const env = getNodeEnv();
    switch (env) {
        case 'staging':
            return '_STAGING';
        case 'production':
            return '_PROD';
        default:
            return '_DEV';
    }
}

/**
 * Environment variable mappings for Next.js static analysis
 * 
 * Next.js requires static analysis for environment variable replacement at build time.
 * We cannot use dynamic access like process.env[varName], so we use direct access
 * through this mapping structure.
 * 
 * To add a new environment variable:
 * 1. Add entry to ENV_VAR_MAP with production, staging, and development values
 * 2. Access via getRequiredPublicEnvVar('YOUR_VAR_NAME')
 */
const ENV_VAR_MAP: Record<string, { production?: string; staging?: string; development?: string }> = {
    API_URL: {
        production: process.env.NEXT_PUBLIC_API_URL_PROD,
        staging: process.env.NEXT_PUBLIC_API_URL_STAGING,
        development: process.env.NEXT_PUBLIC_API_URL_DEV,
    },
    GITHUB_APP_URL: {
        production: process.env.NEXT_PUBLIC_GITHUB_APP_URL_PROD,
        staging: process.env.NEXT_PUBLIC_GITHUB_APP_URL_STAGING,
        development: process.env.NEXT_PUBLIC_GITHUB_APP_URL_DEV,
    },
};

/**
 * Get a required NEXT_PUBLIC environment variable with environment-specific suffix
 * Throws an error if the variable is not set
 * 
 * Note: Uses direct access to process.env because Next.js requires static analysis
 * for environment variable replacement at build time
 */
function getRequiredPublicEnvVar(baseName: string): string {
    const env = getNodeEnv();

    // Check if the baseName is mapped
    const mapping = ENV_VAR_MAP[baseName];
    if (!mapping) {
        const suffix = getEnvSuffix();
        const varName = `NEXT_PUBLIC_${baseName}${suffix}`;
        throw new Error(
            `Environment variable '${baseName}' is not mapped in ENV_VAR_MAP. ` +
            `Add it to ENV_VAR_MAP in env.ts to enable access to ${varName}.`
        );
    }

    // Get value based on current environment
    const value = mapping[env];

    if (!value) {
        const suffix = getEnvSuffix();
        const varName = `NEXT_PUBLIC_${baseName}${suffix}`;
        throw new Error(
            `Missing required environment variable: ${varName} (NODE_ENV=${env})`
        );
    }

    return value;
}

/**
 * Get a required server-side environment variable with environment-specific suffix
 * Throws an error if the variable is not set
 * 
 * Note: Uses direct access to process.env because Next.js requires static analysis
 * for environment variable replacement at build time
 */
function getRequiredServerEnvVar(baseName: string): string {
    const nodeEnv = getNodeEnv();

    // Map baseName to the actual environment variable
    let value: string | undefined;

    if (baseName === 'CLERK_SECRET_KEY') {
        value = nodeEnv === 'production'
            ? process.env.CLERK_SECRET_KEY_PROD
            : process.env.CLERK_SECRET_KEY_DEV;
    }

    if (!value) {
        const suffix = nodeEnv === 'production' ? '_PROD' : '_DEV';
        const varName = `${baseName}${suffix}`;
        throw new Error(
            `Missing required environment variable: ${varName} (NODE_ENV=${nodeEnv})`
        );
    }

    return value;
}

// ============================================================================
// API Configuration
// ============================================================================

/**
 * Get Backend API URL for the current environment
 * Uses: NEXT_PUBLIC_API_URL_DEV, NEXT_PUBLIC_API_URL_STAGING, or NEXT_PUBLIC_API_URL_PROD
 */
export function getApiUrl(): string {
    return getRequiredPublicEnvVar('API_URL');
}

// ============================================================================
// Clerk Configuration
// ============================================================================

/**
 * Get Clerk publishable key for the current environment
 * Uses: NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_DEV for both development AND staging
 *       NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_PROD for production only
 * 
 * Note: Uses direct access to process.env because Next.js requires static analysis
 * for environment variable replacement at build time
 */
export function getClerkPublishableKey(): string {
    const nodeEnv = getNodeEnv();
    const value = nodeEnv === 'production'
        ? process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_PROD
        : process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_DEV;

    if (!value) {
        const suffix = nodeEnv === 'production' ? '_PROD' : '_DEV';
        const varName = `NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY${suffix}`;
        throw new Error(
            `Missing required environment variable: ${varName} (NODE_ENV=${nodeEnv})`
        );
    }

    return value;
}

/**
 * Get Clerk secret key for the current environment (server-side only)
 * Uses: CLERK_SECRET_KEY_DEV for both development AND staging
 *       CLERK_SECRET_KEY_PROD for production only
 */
export function getClerkSecretKey(): string {
    return getRequiredServerEnvVar('CLERK_SECRET_KEY');
}

// ============================================================================
// Environment Summary (for logging/debugging)
// ============================================================================

/**
 * Get a summary of the current environment configuration
 * Useful for logging on app startup
 */
export function getEnvironmentSummary(): {
    nodeEnv: NodeEnv;
    envSuffix: string;
    apiUrl: string;
} {
    const nodeEnv = getNodeEnv();
    return {
        nodeEnv,
        envSuffix: getEnvSuffix(),
        apiUrl: getApiUrl(),
    };
}
